/**
 * @file BlendConvertModal.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-10
 * @modified 2026-09-10
 * @description Turn another supplier's recipe into one we can order and price.
 *
 *              The raw material costs in this calculator are Mole Valley Feed
 *              Solutions supplier costs, so an MVFS formulation (item code
 *              100…) prices directly. Anything else is written in a different
 *              supplier's materials: the inclusions are real, but pricing them
 *              against this book would produce a figure that looks right and
 *              is somebody else's.
 *
 *              So a non-MVFS recipe is held in B, and this turns it into A. Each
 *              ingredient gets our mapped material where we have one, and a
 *              dropdown where we do not — or where the mapping is not the
 *              substitution the user wants to make. Nothing is applied until
 *              they press the button: the point is that the substitutions are a
 *              decision somebody takes, not one the system takes quietly.
 *
 *              An ingredient can also be dropped. A recipe that includes a
 *              premix we simply do not carry is better costed without it and
 *              with the gap visible than with an arbitrary stand-in.
 */

import React, { useMemo, useState } from 'react';
import Modal from './Modal';
import SearchableDropdown from './SearchableDropdown';
import { useSlideOutButton } from './SlideOutButton';
import './BlendConvertModal.css';

const DROP = '__drop__';

/**
 * Body as its own component so useSlideOutButton fires in the same render cycle
 * as the footer buttons it measures — see CLAUDE.md.
 */
function ConvertBody({ rows, choices, setChoice, materialOptions, total, priced }) {
  useSlideOutButton([rows.length]);

  return (
    <div className="bcv-body">
      <p className="bcv-lede">
        Each line of the recipe, with the material we would order in its place.
        Where we already map the ingredient the mapping is filled in; where we do
        not, pick a replacement or leave it out.
      </p>

      <div className="bcv-scroll">
        <table className="data-table bcv-table">
          <thead>
            <tr>
              <th>Ingredient</th>
              <th className="bcv-pct">%</th>
              <th>Our material</th>
              <th className="bcv-state">Cost</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const chosen = choices[r.key];
              const material = chosen && chosen !== DROP
                ? materialOptions.find((o) => o.value === chosen) : null;
              return (
                <tr key={r.key} className={chosen === DROP ? 'bcv-dropped' : ''}>
                  <td>
                    <span className="bcv-name">{r.label}</span>
                    {r.suggested && chosen === r.suggested && (
                      <span className="bcv-tag bcv-tag-auto">Our mapping</span>
                    )}
                    {!r.suggested && chosen && chosen !== DROP && (
                      <span className="bcv-tag bcv-tag-manual">Your choice</span>
                    )}
                  </td>
                  <td className="bcv-pct">{r.value}</td>
                  <td>
                    <SearchableDropdown
                      options={materialOptions}
                      value={chosen ?? DROP}
                      onChange={(v) => setChoice(r.key, v)}
                      placeholder="Leave out"
                      searchPlaceholder="Search materials…"
                    />
                  </td>
                  <td className="bcv-state">
                    {chosen === DROP || !chosen
                      ? <span className="bcv-tag bcv-tag-out">Left out</span>
                      : material?.priced
                        ? <span className="bcv-tag bcv-tag-ok">Priced</span>
                        : <span className="bcv-tag bcv-tag-warn">No price</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* The two numbers that decide whether this is worth applying. */}
      <div className="bcv-summary">
        <span className={`bcv-total${Math.abs(total - 100) > 0.01 ? ' is-off' : ''}`}>
          {total.toFixed(2)}% of the recipe carried over
        </span>
        <span className="bcv-priced">
          {priced} of {rows.length} lines will price
        </span>
      </div>
    </div>
  );
}

function BlendConvertModal({ source, materials = [], priceColumnKey, onApply, onClose }) {
  const rows = useMemo(() => (source?.components || [])
    .map((c) => {
      // One candidate is a mapping we can act on. Several is a real ambiguity
      // and the user has to choose, so nothing is pre-selected — picking one
      // arbitrarily is how a wrong substitution gets made silently.
      const single = c.candidates?.length === 1 ? c.candidates[0] : null;
      return {
        key: c.key,
        label: c.label,
        value: c.value,
        suggested: single?.material_key || null,
        ambiguous: (c.candidates?.length || 0) > 1
      };
    })
    .sort((a, b) => b.value - a.value), [source]);

  const [choices, setChoices] = useState(
    () => Object.fromEntries(rows.map((r) => [r.key, r.suggested || DROP])));

  const setChoice = (key, value) => setChoices((prev) => ({ ...prev, [key]: value }));

  const materialOptions = useMemo(() => [
    { value: DROP, label: 'Leave out', searchText: 'leave out none drop skip omit' },
    ...materials.map((m) => {
      // Priced in the column being quoted on, not merely priced somewhere: a
      // material with a Spot cost and no contract cost cannot price a contract
      // quote, and saying "Priced" there would be a lie.
      const cost = priceColumnKey ? m.costs?.[priceColumnKey] : null;
      const hasCost = cost !== null && cost !== undefined && Number.isFinite(Number(cost));
      return {
        value: m.key,
        label: m.name,
        priced: hasCost,
        badge: hasCost ? null : 'no price',
        searchText: [m.name, m.key, m.category].filter(Boolean).join(' ')
      };
    })
  ], [materials, priceColumnKey]);

  const applied = useMemo(() => {
    const out = {};
    for (const r of rows) {
      const choice = choices[r.key];
      if (!choice || choice === DROP) continue;
      // Two ingredients can map to the same material — molasses reaches
      // Molasses from two component keys — so inclusions ADD rather than the
      // last one winning.
      out[choice] = (out[choice] || 0) + Number(r.value || 0);
    }
    return out;
  }, [rows, choices]);

  const total = useMemo(
    () => Object.values(applied).reduce((s, v) => s + v, 0), [applied]);

  const pricedCount = useMemo(() => rows.filter((r) => {
    const c = choices[r.key];
    if (!c || c === DROP) return false;
    return materialOptions.find((o) => o.value === c)?.priced;
  }).length, [rows, choices, materialOptions]);

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={`Rebuild ${source?.name || 'this recipe'} from our materials`}
      size="lg"
      className="blend-convert"
      footer={
        <div className="buttons slideout">
          <div>
            <button data-read-safe className="btn-round btn-danger" onClick={onClose}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
              <span className="btn-label">Cancel</span>
            </button>
          </div>
          <div>
            <button
              className="btn-slideout"
              onClick={() => onApply(applied)}
              disabled={!Object.keys(applied).length}
              title={Object.keys(applied).length
                ? 'Put these materials on the bench as A'
                : 'Choose at least one material first'}
            >
              <div className="icon-wrapper"><span className="material-symbols-rounded">move_down</span></div>
              <span className="btn-label">Build it</span>
            </button>
          </div>
        </div>
      }
    >
      <ConvertBody
        rows={rows}
        choices={choices}
        setChoice={setChoice}
        materialOptions={materialOptions}
        total={total}
        priced={pricedCount}
      />
    </Modal>
  );
}

export default BlendConvertModal;
